import React, { useState, useEffect } from 'react';
import { X, Send, CheckCircle2 } from 'lucide-react';

// API URL for CRA
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ContactForm = ({ isOpen, onClose, selectedService = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: selectedService,
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      service: selectedService,
    }));
  }, [selectedService]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (data.success) {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: selectedService,
          message: '',
        });
        
        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 2000);
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
      console.error('Contact form error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-zinc-900 border border-zinc-800 w-full max-w-2xl relative my-8">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 md:top-6 md:right-6 text-zinc-400 hover:text-white transition-colors z-10 bg-zinc-800 hover:bg-zinc-700 p-2 rounded-full"
          aria-label="Close"
        >
          <X size={24} strokeWidth={2} />
        </button>

        {/* Header */}
        <div className="p-6 md:p-8 border-b border-zinc-800">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-light uppercase tracking-tight text-white pr-8">
            Get In Touch
          </h2>
          <p className="text-zinc-400 mt-2 font-light text-sm md:text-base">
            Fill out the form below and we'll get back to you shortly.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="p-6 md:p-8">
          {success ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircle2 size={64} className="text-green-500 mb-4" />
              <h3 className="text-xl md:text-2xl text-white mb-2">Message Sent!</h3>
              <p className="text-zinc-400">We'll get back to you soon.</p>
            </div>
          ) : (
            <div className="space-y-5 md:space-y-6">
              {/* Name */}
              <div>
                <label className="block text-xs md:text-sm uppercase tracking-widest text-zinc-500 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-black border border-zinc-800 px-3 py-2.5 md:px-4 md:py-3 text-white text-sm md:text-base focus:border-white focus:outline-none transition-colors"
                  placeholder="John Doe"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs md:text-sm uppercase tracking-widest text-zinc-500 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-black border border-zinc-800 px-3 py-2.5 md:px-4 md:py-3 text-white text-sm md:text-base focus:border-white focus:outline-none transition-colors"
                  placeholder="john@example.com"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs md:text-sm uppercase tracking-widest text-zinc-500 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-black border border-zinc-800 px-3 py-2.5 md:px-4 md:py-3 text-white text-sm md:text-base focus:border-white focus:outline-none transition-colors"
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              {/* Service */}
              <div>
                <label className="block text-xs md:text-sm uppercase tracking-widest text-zinc-500 mb-2">
                  Service Interested In
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full bg-black border border-zinc-800 px-3 py-2.5 md:px-4 md:py-3 text-white text-sm md:text-base focus:border-white focus:outline-none transition-colors"
                >
                  <option value="">Select a service</option>
                  <option value="Celebrity Management">Celebrity Management</option>
                  <option value="Event Management">Event Management</option>
                  <option value="Club Events">Club Events</option>
                  <option value="Private Events">Private Events</option>
                  <option value="Production">Production</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs md:text-sm uppercase tracking-widest text-zinc-500 mb-2">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full bg-black border border-zinc-800 px-3 py-2.5 md:px-4 md:py-3 text-white text-sm md:text-base focus:border-white focus:outline-none transition-colors resize-none"
                  placeholder="Tell us about your project..."
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 md:p-4 bg-red-500/10 border border-red-500/50 text-red-500 text-xs md:text-sm">
                  {error}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 md:gap-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full bg-zinc-800 text-white py-3 md:py-4 uppercase tracking-widest text-xs md:text-sm font-medium hover:bg-zinc-700 transition-colors flex items-center justify-center gap-2"
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-white text-black py-3 md:py-4 uppercase tracking-widest text-xs md:text-sm font-medium hover:bg-zinc-200 transition-colors disabled:bg-zinc-700 disabled:text-zinc-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send size={16} />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ContactForm;